<template>
  <Modal v-bind="getBindValue" @cancel="handleCancel">
    <template v-if="!slots.closeIcon" #closeIcon>
      <ModalClose :canFullscreen="false" @cancel="handleCancel" />
    </template>

    <template v-if="!slots.title" #title>
      <ModalHeader :helpMessage="getProps.helpMessage" :title="getMergeProps.title" />
    </template>

    <template v-if="!slots.footer" #footer>
      <ModalFooter v-bind="getBindValue" @ok="emit('ok', $event)" @cancel="handleCancel">
        <template v-for="item in Object.keys(slots)" #[item]="data">
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </ModalFooter>
    </template>

    <ModalWrapper
      ref="modalWrapperRef"
      :useWrapper="getProps.useWrapper"
      :footerOffset="getProps.wrapperFooterOffset"
      :loading="getProps.loading"
      :loading-tip="getProps.loadingTip"
      :minHeight="getProps.minHeight"
      :height="getProps.height"
      :modalId="getProps.modalId"
      :open="openRef"
      :modalFooterHeight="footer !== undefined && !footer ? 0 : undefined"
      v-bind="omit(getProps.wrapperProps, ['open', 'height', 'modalFooterHeight'])"
      @height-change="handleHeightChange"
    >
      <slot></slot>
    </ModalWrapper>

    <template v-for="item in Object.keys(omit(slots, ['default']))" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
  </Modal>
</template>
<script lang="ts" setup>
  import { Modal } from 'ant-design-vue';
  import {
    computed,
    getCurrentInstance,
    nextTick,
    ref,
    unref,
    useAttrs,
    useSlots,
    watch,
  } from 'vue';
  import { isFunction, omit } from '@mts-fe/utils';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { getModalSize, inMtsApp } from '@mts/utils';
  import ModalClose from './components/ModalClose.vue';
  import ModalFooter from './components/ModalFooter.vue';
  import ModalHeader from './components/ModalHeader.vue';
  import ModalWrapper from './components/ModalWrapper.vue';
  import { basicProps } from './props';
  import type { ModalMethods, ModalProps } from './typing';

  defineOptions({ name: 'SimpleModal', inheritAttrs: false });

  const props = defineProps(basicProps);

  const emit = defineEmits(['height-change', 'cancel', 'ok', 'register', 'update:open']);

  const attrs = useAttrs();
  const slots = useSlots();
  const openRef = ref(false);
  const propsRef = ref<Partial<ModalProps> | null>(null);
  const modalWrapperRef = ref<any>(null);
  const { prefixCls } = useDesign('basic-modal');

  const modalMethods: ModalMethods = {
    setModalProps,
    emitOpen: undefined,
    redoModalHeight: () => {
      nextTick(() => unref(modalWrapperRef)?.setModalHeight());
    },
  };

  const instance = getCurrentInstance();
  if (instance) {
    emit('register', modalMethods, instance.uid);
  }

  const getMergeProps = computed(() => {
    const mergeProps = { ...props, ...(unref(propsRef) as any) } as Recordable;
    if (mergeProps.size) {
      Object.assign(mergeProps, getModalSize(mergeProps.size));
    }
    return mergeProps;
  });

  const getProps = computed((): Recordable => {
    const mergedProps = unref(getMergeProps);
    return {
      ...mergedProps,
      open: unref(openRef),
      okButtonProps: undefined,
      cancelButtonProps: undefined,
      title: undefined,
    };
  });

  const transitions = inMtsApp ? { transitionName: '', maskTransitionName: '' } : {};
  const getBindValue = computed(() => {
    const attr = { ...transitions, ...attrs, ...getMergeProps.value, open: openRef.value };

    const existingClassName = attr?.['wrapClassName'] || '';
    attr['wrapClassName'] = existingClassName ? `${existingClassName} ${prefixCls}` : prefixCls;
    return omit(attr, ['height', 'title']);
  });

  watch(
    () => props.open,
    (newVal) => {
      openRef.value = !!newVal;
    },
    { immediate: true },
  );

  watch(
    () => unref(openRef),
    (isOpen, prevOpen) => {
      if (isOpen === prevOpen) return;

      emit('update:open', isOpen);

      if (instance && modalMethods.emitOpen) {
        modalMethods.emitOpen(isOpen, instance.uid);
      }

      if (props.scrollTop && isOpen) {
        nextTick(() => unref(modalWrapperRef)?.scrollTop?.());
      }
    },
  );

  const handleCancel = async (e: Event) => {
    e?.stopPropagation();

    const target = e.target as HTMLElement;
    if (target?.classList?.contains(`${prefixCls}-close--custom`)) return;

    if (props.closeFunc && isFunction(props.closeFunc)) {
      const isClose: boolean = await props.closeFunc();
      openRef.value = !isClose;
      return;
    }

    openRef.value = false;
    emit('cancel', e);
  };

  function setModalProps(props: Partial<ModalProps>) {
    let mergedProps = { ...(propsRef.value as any) };

    if ('footer' in props && typeof props.footer === 'undefined') {
      const { footer, ...rest } = props;
      mergedProps = { ...mergedProps, ...rest };
      delete mergedProps.footer;
    } else {
      mergedProps = { ...mergedProps, ...props };
    }
    propsRef.value = mergedProps;

    if (Reflect.has(props, 'open')) {
      openRef.value = !!props.open;
    }
  }

  const handleHeightChange = (height: string) => {
    emit('height-change', height);
  };
</script>
